import '../../i18n/autoTranslate';
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const OverallScoreCard = ({ 
  overallScore = 85,
  sessionType = 'Technical Interview',
  duration = '45 minutes',
  completedAt = new Date(),
  isLoading = false
}) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setAnimatedScore(overallScore);
        if (overallScore >= 80) {
          setShowCelebration(true);
          setTimeout(() => setShowCelebration(false), 2000);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [overallScore, isLoading]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-indigo-400';
    return 'text-yellow-400';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-green-400 to-green-600';
    if (score >= 60) return 'from-indigo-400 to-indigo-600';
    return 'from-yellow-400 to-yellow-600';
  };

  const getPerformanceLevel = (score) => {
    if (score >= 90) return 'Exceptional';
    if (score >= 80) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Fair';
    return 'Needs Improvement';
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  if (isLoading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
          <div className="text-center md:text-left space-y-4">
            <div className="h-8 bg-gray-700/50 rounded-lg w-48"></div>
            <div className="h-4 bg-gray-700/50 rounded w-32"></div>
          </div>
          <div className="flex items-center justify-center w-32 h-32 rounded-full bg-gray-700/30"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative p-8 overflow-hidden">
      {/* Celebration Animation */}
      {showCelebration && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-4 left-4 animate-bounce">
            <Icon name="Star" size={24} className="text-indigo-400 animate-pulse" />
          </div>
          <div className="absolute top-8 right-8 animate-bounce" style={{ animationDelay: '0.2s' }}>
            <Icon name="Trophy" size={20} className="text-green-400 animate-pulse" />
          </div>
          <div className="absolute bottom-8 left-8 animate-bounce" style={{ animationDelay: '0.4s' }}>
            <Icon name="Award" size={18} className="text-indigo-400 animate-pulse" />
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
        {/* Session Info */}
        <div className="text-center md:text-left space-y-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Interview Complete!
            </h2>
            <p className="text-lg text-gray-300">{sessionType}</p>
          </div>

          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} />
              <span>Duration: {duration}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} />
              <span>Completed: {formatDate(completedAt)}</span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-400">Performance Level:</span>
            <span className={`text-sm font-bold ${getScoreColor(animatedScore)}`}>
              {getPerformanceLevel(animatedScore)}
            </span>
          </div>
        </div>

        {/* Score Display */}
        <div className="relative">
          <div className="flex items-center justify-center w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-gray-800/50 to-gray-900/30 border-4 border-gray-700/50">
            <div className="text-center">
              <div className={`text-4xl md:text-5xl font-bold ${getScoreColor(animatedScore)} transition-all duration-1000 ease-out`}>
                {Math.round(animatedScore)}
              </div>
              <div className="text-sm text-gray-400 font-medium">
                Overall Score
              </div>
            </div>
          </div>

          {/* Score Ring Animation */}
          <svg className="absolute inset-0 w-32 h-32 md:w-40 md:h-40 transform -rotate-90">
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              fill="none"
              stroke="rgba(148, 163, 184, 0.2)"
              strokeWidth="3"
            />
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              fill="none"
              stroke="url(#scoreGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 45} ${2 * Math.PI * 45}`}
              strokeDashoffset={`${2 * Math.PI * 45 * (1 - animatedScore / 100)}`}
              className="transition-all duration-2000 ease-out"
            />
            <defs>
              <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={animatedScore >= 80 ? '#10B981' : animatedScore >= 60 ? '#6366F1' : '#F59E0B'} />
                <stop offset="100%" stopColor={animatedScore >= 80 ? '#059669' : animatedScore >= 60 ? '#4F46E5' : '#D97706'} />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 pt-6 border-t border-gray-700">
        <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm text-gray-400">
              <Icon name="TrendingUp" size={16} />
              <span>
                {animatedScore >= 80 ? 'Great improvement!' : 'Keep practicing to improve'}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white transition-all duration-200 border border-gray-700/50">
              <Icon name="Share2" size={16} />
              <span className="text-sm font-medium">Share</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:shadow-lg transition-all duration-200">
              <Icon name="Download" size={16} />
              <span className="text-sm font-medium">Download Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverallScoreCard;
